import { Division, EntityLabel, Region, Site, Subdivision } from '@esg/esg-global-types';
import { auth } from '../google/firebase';
import { getSubdivisions } from './subdivision';
import { getDivisions } from './division';
import { generateUuidV4 } from '../../util/uuid';
import { MetadataError } from '@ep/error-handling';
import { apollo_client } from '../app/db_util';
import { isValidLatitude, isValidLongitude } from '../validation/geo_validation';
import { ALLOW_DELETE_SITE_QUERY, GET_SITES_QUERY } from '../graphql/queries/site';
import {
  ARCHIVE_SITE_MUTATOR,
  CREATE_SITE_MUTATOR,
  DELETE_IMPORTED_SITES_BY_TRACKING_ID,
  DELETE_SITE_MUTATOR,
  UNARCHIVE_SITE_MUTATOR,
  UPDATE_SITE_MUTATOR
} from '../graphql/mutations/site';
import { DocumentReference } from 'firebase/firestore';
import { getRegions } from './region';

export interface SiteExtended extends Site {
  division: Division;
  subdivision: Subdivision;
  region: Region;
}

/**
 * Type guard — narrows an unknown entity to SiteExtended.
 */
export function isSiteExtended(entity: unknown): entity is SiteExtended {
  return (
    typeof entity === 'object' &&
    entity !== null &&
    'id' in entity &&
    'name' in entity &&
    'region' in entity &&
    'division' in entity &&
    'subdivision' in entity &&
    typeof entity.region === 'object' &&
    entity.region !== null &&
    'name' in entity.region
  );
}

export const site_label: EntityLabel = {
  one: 'Site',
  many: 'Sites'
};

/**
 * Query all site documents for current portal company.
 * @param {Group} group Group object of current portal company.
 * @param {string} company_id id of current portal company to reference firestore documents.
 * @returns {Promise<Array<Site>>}
 */
export const getSites = async (group_id: string, company_id: string): Promise<Array<Site>> => {
  try {
    const sites: Array<Site> = await apollo_client
      .query({
        variables: {
          group_id: group_id,
          company_id: company_id
        },
        query: GET_SITES_QUERY,
        fetchPolicy: 'no-cache'
      })
      .then((response) => {
        return response.data.sites;
      });
    return sites;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/app/site.ts failed on an unknown error while calling getSites.',
      {
        group_id: group_id,
        company_id: company_id
      },
      tracking_id
    );
  }
};

/**
 * Query all site documents for current portal company joined with relative division and subdivision data
 * @param {string} group_id Group ID of current portal company.
 * @param {string} company_id Id of current portal company to reference firestore documents.
 * @returns {Promise<Array<SiteExtended>>}
 */
export const getSitesJoinParents = async (
  group_id: string,
  company_id: string
): Promise<Array<SiteExtended>> => {
  try {
    const sites_arr: Array<SiteExtended> = [];
    const [regions, divisions, subdivisions, sites]: [
      Array<Region>,
      Array<Division>,
      Array<Subdivision>,
      Array<Site>
    ] = await Promise.all([
      getRegions(false, group_id, company_id),
      getDivisions(group_id, company_id),
      getSubdivisions(group_id, company_id),
      getSites(group_id, company_id)
    ]);
    sites.forEach((site: Site) => {
      const region: Region | undefined = regions.find(
        (region: Region) => region.id === site.region.id
      );
      const subdivision: Subdivision | undefined = subdivisions.find(
        (subdivision: Subdivision) => subdivision.id === site.subdivision.id
      );
      const division: Division | null | undefined = subdivision
        ? divisions.find((division: Division) => division.id === subdivision.division.id)
        : null;
      division &&
        subdivision &&
        region &&
        sites_arr.push({
          ...site,
          region: region,
          division: division,
          subdivision: subdivision
        });
    });
    return sites_arr;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/app/site.ts failed on an unknown error while calling getSitesJoinParents.',
      {
        group_id: group_id,
        company_id: company_id
      },
      tracking_id
    );
  }
};

/**
 * Create site with relative data.
 * @param {string} group_id ID of Group which Site is being created for.
 * @param {string} company_id ID of Company which Site is being created for.
 * @param {string} site_name Name of new Subdivision.
 * @param {Subdivision} subdivision Group Subdivision of Site.
 * @param {Region} region Group Region of Site.
 * @param {string | undefined} latitude Optional latitude GPS coordinate of Site.
 * @param {string | undefined} longitude Optional longitude GPS coordinate of Site.
 * @param {string | undefined} glo_id Optionally set matching GLO BigQuery ID to Site.
 * @returns {DocumentReference}
 */
export const createSite = async (
  group_id: string,
  company_id: string,
  site_name: string,
  subdivision: Subdivision,
  region: Region,
  latitude?: string,
  longitude?: string,
  glo_id?: string
): Promise<DocumentReference> => {
  try {
    if (
      group_id &&
      company_id &&
      site_name &&
      subdivision &&
      region &&
      (!latitude || isValidLatitude(latitude)) &&
      (!longitude || isValidLongitude(longitude))
    ) {
      const site_doc: DocumentReference = await apollo_client
        .mutate({
          mutation: CREATE_SITE_MUTATOR,
          variables: {
            group_id: group_id,
            company_id: company_id,
            site_name: site_name,
            subdivision: subdivision,
            region: region,
            latitude: latitude,
            longitude: longitude,
            glo_id: glo_id,
            user_email: auth.currentUser?.email
          }
        })
        .then((response) => {
          return response.data.createSite;
        });
      return site_doc;
    } else {
      throw new MetadataError('Invalid parameters provided.', {
        group_id: group_id,
        company_id: company_id,
        site_name: site_name,
        subdivision: subdivision,
        region: region,
        latitude_str: latitude,
        longitude_str: longitude,
        glo_id: glo_id
      });
    }
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/app/site.ts failed on an unknown error while calling createSite.',
      {
        group_id: group_id,
        company_id: company_id,
        site_name: site_name,
        subdivision: subdivision,
        region: region,
        latitude: latitude,
        longitude: longitude,
        glo_id: glo_id
      },
      tracking_id
    );
  }
};

/**
 * Update site with relative data.
 * @param {string} group_id ID of Group to update site for.
 * @param {string} company_id ID of Company to update site for.
 * @param {Site} updated_site New Site data to push into document.
 * @param {Site} original_data Old Site data to use for audit logs.
 * @returns {Promise<void>}
 */
export const updateSite = async (
  group_id: string,
  company_id: string,
  updated_site: Site,
  original_data: Site
): Promise<void> => {
  try {
    if (
      group_id &&
      company_id &&
      updated_site.name &&
      updated_site.subdivision &&
      updated_site.region &&
      (!updated_site.latitude || isValidLatitude(updated_site.latitude)) &&
      (!updated_site.longitude || isValidLongitude(updated_site.longitude))
    ) {
      await apollo_client
        .mutate({
          mutation: UPDATE_SITE_MUTATOR,
          variables: {
            group_id: group_id,
            company_id: company_id,
            updated_site: updated_site,
            original_data: original_data,
            user_email: auth.currentUser?.email
          }
        })
        .then((response) => {
          return response.data.updateSite;
        });
    } else {
      throw new MetadataError('Invalid parameters provided.', {
        group_id: group_id,
        company_id: company_id,
        site_name: updated_site.name,
        subdivision: updated_site.subdivision,
        region: updated_site.region,
        latitude_str: updated_site.latitude,
        longitude_str: updated_site.longitude
      });
    }
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/app/site.ts failed on an unknown error while calling updateSite.',
      {
        group_id: group_id,
        company_id: company_id,
        updated_site: updated_site,
        original_data: original_data
      },
      tracking_id
    );
  }
};

/**
 * Delete site.
 * @param {string} group_id ID of Group to delete site for.
 * @param {string} company_id ID of Company to delete site for.
 * @param {string} site_id ID of site to delete.
 * @param {string} site_name Name of site being deleted for audit logs.
 * @returns {Promise<void>}
 */
export const deleteSite = async (
  group_id: string,
  company_id: string,
  site_id: string,
  site_name: string
): Promise<void> => {
  try {
    if (group_id && company_id && site_id) {
      await apollo_client
        .mutate({
          mutation: DELETE_SITE_MUTATOR,
          variables: {
            group_id: group_id,
            company_id: company_id,
            site_id: site_id,
            site_name: site_name,
            user_email: auth.currentUser?.email
          }
        })
        .then((response) => {
          return response.data.deleteSite;
        });
    }
    return;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/app/site.ts failed on an unknown error while calling deleteSite.',
      {
        group_id: group_id,
        company_id: company_id,
        site_id: site_id,
        site_name: site_name
      },
      tracking_id
    );
  }
};

/**
 * Function to archive site
 * @param {string} group_id ID of Group to archive site for
 * @param {string} company_id ID of Company to archive site for
 * @param {string} site_id ID of site to archive
 * @returns {void}
 */
export const archiveSite = async (group_id: string, company_id: string, site_id: string) => {
  try {
    if (group_id && company_id && site_id) {
      await apollo_client
        .mutate({
          mutation: ARCHIVE_SITE_MUTATOR,
          variables: {
            group_id: group_id,
            company_id: company_id,
            site_id: site_id,
            user_email: auth.currentUser?.email
          }
        })
        .then((response) => {
          return response.data.archiveSite;
        });
    }
    return;
  } catch (err) {
    const error = `Error while archiving Site in Firebase: ${JSON.stringify({
      message: err instanceof Error ? err.message : '',
      stacktrace: err instanceof Error ? err.stack : ''
    })}`;
    throw new Error(`Error: archiveSite: ${JSON.stringify(error)}.`);
  }
};

/**
 * Archive site
 * @param {string} group_id ID of Group to unarchive site for
 * @param {string} company_id ID of Company to unarchive site for
 * @param {string} site_id ID of site to unarchive
 * @returns {Promise<void>}
 */
export const unarchiveSite = async (
  group_id: string,
  company_id: string,
  site_id: string
): Promise<void> => {
  try {
    if (group_id && company_id && site_id) {
      await apollo_client
        .mutate({
          mutation: UNARCHIVE_SITE_MUTATOR,
          variables: {
            group_id: group_id,
            company_id: company_id,
            site_id: site_id,
            user_email: auth.currentUser?.email
          }
        })
        .then((response) => {
          return response.data.unarchiveSite;
        });
    }
    return;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/app/site.ts failed on an unknown error while calling archiveSite.',
      {
        group_id: group_id,
        company_id: company_id,
        site_id: site_id
      },
      tracking_id
    );
  }
};

/**
 * Check if a Site can be safely deleted
 * @param {string} group_id ID of Group to check Site for
 * @param {string} company_id ID of Company to check Site for
 * @param {string} site_id ID of site to check
 * @returns {Promise<boolean>}
 */
export const allowDeleteSite = async (
  group_id: string,
  company_id: string,
  site_id: string
): Promise<boolean> => {
  try {
    const allow_delete = await apollo_client
      .query({
        variables: {
          group_id: group_id,
          company_id: company_id,
          site_id: site_id
        },
        query: ALLOW_DELETE_SITE_QUERY,
        fetchPolicy: 'no-cache'
      })
      .then((response) => {
        return response.data.allowDeleteSite;
      });
    return allow_delete;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/app/site.ts failed on an unknown error while calling allowDeleteSite.',
      {
        group_id: group_id,
        company_id: company_id,
        site_id: site_id
      },
      tracking_id
    );
  }
};

/**
 * Delete imported sites and completed document based on the import tracking id.
 * @param {string} group_id ID of Group which site import is being deleted for.
 * @param {string} company_id ID of Company which site import is being deleted for.
 * @param {string} tracking_id ID use to track related import documents.
 * @returns {Promise<void>}
 */
export const deleteImportedSitesByTrackingId = async (
  group_id: string,
  company_id: string,
  tracking_id: string
): Promise<void> => {
  try {
    await apollo_client
      .mutate({
        mutation: DELETE_IMPORTED_SITES_BY_TRACKING_ID,
        variables: {
          group_id: group_id,
          company_id: company_id,
          tracking_id: tracking_id,
          user_email: auth.currentUser?.email
        }
      })
      .then((response) => {
        return response.data.deleteSitesByTrackingId;
      });
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/metric_capture/site.ts failed on an unknown error while calling deleteImportedSitesByTrackingId.',
      {
        group_id: group_id,
        company_id: company_id,
        tracking_id: tracking_id
      },
      tracking_id
    );
  }
};
